/**
 * Volleyball Score Logic — Dinamo Molfetta
 *
 * Regole FIPAV:
 * - Set vinto a 25 punti con +2 di vantaggio
 * - 5° set (tiebreak) vinto a 15 punti con +2
 * - Partita vinta a 3 set (best of 5)
 * - Al punto segnato il servizio passa al team che ha segnato
 * - Max 2 timeout per squadra per set (reset ad ogni nuovo set)
 */

import { MatchState, MatchStatus, defaultMatchState } from '@/types/match'

// ─── Costanti ─────────────────────────────────────────────────────────────────

const SET_WIN_SCORE     = 25
const TIEBREAK_WIN_SCORE = 15
const MIN_ADVANTAGE    = 2
const SETS_TO_WIN_MATCH = 3
const MAX_SETS         = 5
const MAX_TIMEOUTS     = 2

// ─── Helpers interni ──────────────────────────────────────────────────────────

function isTiebreakSet(setNumber: number): boolean {
  return setNumber === MAX_SETS
}

function winScore(setNumber: number): number {
  return isTiebreakSet(setNumber) ? TIEBREAK_WIN_SCORE : SET_WIN_SCORE
}

function isSetWon(teamScore: number, opponentScore: number, setNumber: number): boolean {
  const target = winScore(setNumber)
  return teamScore >= target && teamScore - opponentScore >= MIN_ADVANTAGE
}

function isMatchWon(setsWon: { home: number; away: number }): 'home' | 'away' | null {
  if (setsWon.home >= SETS_TO_WIN_MATCH) return 'home'
  if (setsWon.away >= SETS_TO_WIN_MATCH) return 'away'
  return null
}

// ─── Azioni ───────────────────────────────────────────────────────────────────

export type ScoreAction =
  | { type: 'ADD_POINT';      team: 'home' | 'away' }
  | { type: 'REMOVE_POINT';   team: 'home' | 'away' }
  | { type: 'SET_SERVICE';    team: 'home' | 'away' }
  | { type: 'END_SET' }
  | { type: 'RESET_MATCH' }
  | { type: 'UNDO_LAST_POINT' }
  | { type: 'SET_STATUS';     status: MatchStatus }
  | { type: 'CALL_TIMEOUT';   team: 'home' | 'away' }
  | { type: 'END_TIMEOUT' }
  | { type: 'SET_TEAM_INFO';  side: 'home' | 'away'; name?: string; logo?: string }
  | { type: 'SET_SCHEDULED_AT'; isoDate: string }
  | { type: 'SET_STREAM_URL'; url: string }
  | { type: 'SET_COMMENTARY_PANEL'; visible: boolean }

export interface ScoreResult {
  state: MatchState
  event: 'point' | 'set_end' | 'match_end' | 'service_set' | 'status_change' | 'unchanged'
  setJustEnded?:   { setNumber: number; winner: 'home' | 'away' }
  matchJustEnded?: { winner: 'home' | 'away' }
}

export function applyScoreAction(state: MatchState, action: ScoreAction): ScoreResult {
  switch (action.type) {
    case 'SET_SERVICE':      return applySetService(state, action.team)
    case 'ADD_POINT':        return applyAddPoint(state, action.team)
    case 'REMOVE_POINT':     return applyRemovePoint(state, action.team)
    case 'END_SET':          return applyEndSet(state)
    case 'UNDO_LAST_POINT':  return applyUndoLastPoint(state)
    case 'RESET_MATCH':      return applyReset(state)
    case 'SET_STATUS':       return applySetStatus(state, action.status)
    case 'CALL_TIMEOUT':     return applyCallTimeout(state, action.team)
    case 'END_TIMEOUT':      return applyEndTimeout(state)
    case 'SET_TEAM_INFO':    return applySetTeamInfo(state, action.side, action.name, action.logo)
    case 'SET_SCHEDULED_AT': return { state: { ...state, scheduledAt: action.isoDate }, event: 'unchanged' }
    case 'SET_STREAM_URL':   return { state: { ...state, streamUrl: action.url }, event: 'unchanged' }
    case 'SET_COMMENTARY_PANEL':
      return { state: { ...state, scorebugCommentaryVisible: action.visible }, event: 'unchanged' }
    default:                 return { state, event: 'unchanged' }
  }
}

// ─── Handlers ────────────────────────────────────────────────────────────────

function applyReset(state: MatchState): ScoreResult {
  return {
    state: {
      ...defaultMatchState,
      matchId:        state.matchId,
      homeTeam:       state.homeTeam,
      awayTeam:       state.awayTeam,
      homeTeamLogo:   state.homeTeamLogo,
      awayTeamLogo:   state.awayTeamLogo,
      scheduledAt:    state.scheduledAt,
      streamUrl:      state.streamUrl,
    },
    event: 'unchanged',
  }
}

function applySetService(state: MatchState, team: 'home' | 'away'): ScoreResult {
  return {
    state: { ...state, serviceTeam: team, firstServer: state.firstServer ?? team },
    event: 'service_set',
  }
}

function applyAddPoint(state: MatchState, team: 'home' | 'away'): ScoreResult {
  if (state.isMatchFinished) return { state, event: 'unchanged' }

  const newScore = {
    home: state.currentScore.home + (team === 'home' ? 1 : 0),
    away: state.currentScore.away + (team === 'away' ? 1 : 0),
  }
  const newState: MatchState = {
    ...state,
    currentScore: newScore,
    serviceTeam:  team,
    status: state.status === 'scheduled' || state.status === 'warmup'
      ? 'live'
      : state.status,
  }

  const teamScore     = newScore[team]
  const opponentScore = newScore[team === 'home' ? 'away' : 'home']

  if (isSetWon(teamScore, opponentScore, state.currentSet)) {
    return applyEndSet(newState)
  }
  return { state: newState, event: 'point' }
}

function applyRemovePoint(state: MatchState, team: 'home' | 'away'): ScoreResult {
  if (state.currentScore[team] <= 0) return { state, event: 'unchanged' }
  return {
    state: {
      ...state,
      currentScore: {
        home: state.currentScore.home - (team === 'home' ? 1 : 0),
        away: state.currentScore.away - (team === 'away' ? 1 : 0),
      },
    },
    event: 'unchanged',
  }
}

function applyEndSet(state: MatchState): ScoreResult {
  const { home, away } = state.currentScore
  if (home === away) return { state, event: 'unchanged' }
  const winner: 'home' | 'away' = home > away ? 'home' : 'away'

  const newSetsWon = {
    home: state.setsWon.home + (winner === 'home' ? 1 : 0),
    away: state.setsWon.away + (winner === 'away' ? 1 : 0),
  }
  const setJustEnded = { setNumber: state.currentSet, winner }
  const matchWinner  = isMatchWon(newSetsWon)

  if (matchWinner) {
    return {
      state: {
        ...state,
        setHistory:      [...state.setHistory, { home, away }],
        setsWon:         newSetsWon,
        currentScore:    { home: 0, away: 0 },
        isMatchFinished: true,
        winner:          matchWinner,
        status:          'finished',
      },
      event: 'match_end',
      setJustEnded,
      matchJustEnded: { winner: matchWinner },
    }
  }

  const nextSet = state.currentSet + 1
  return {
    state: {
      ...state,
      setHistory:    [...state.setHistory, { home, away }],
      setsWon:       newSetsWon,
      currentScore:  { home: 0, away: 0 },
      currentSet:    nextSet,
      isTiebreak:    isTiebreakSet(nextSet),
      serviceTeam:   null,
      firstServer:   null,
      timeoutsUsed:  { home: 0, away: 0 },   // reset timeout ad ogni set
      status:        'pause',
    },
    event: 'set_end',
    setJustEnded,
  }
}

function applyUndoLastPoint(state: MatchState): ScoreResult {
  if (!state.serviceTeam) return { state, event: 'unchanged' }
  return applyRemovePoint(state, state.serviceTeam)
}

function applySetStatus(state: MatchState, status: MatchStatus): ScoreResult {
  const updates: Partial<MatchState> = { status }
  if (status === 'live' && !state.startedAt) updates.startedAt = new Date().toISOString()
  return { state: { ...state, ...updates }, event: 'status_change' }
}

function applyCallTimeout(state: MatchState, team: 'home' | 'away'): ScoreResult {
  if (state.status !== 'live') return { state, event: 'unchanged' }
  const used = state.timeoutsUsed[team]
  if (used >= MAX_TIMEOUTS) return { state, event: 'unchanged' }  // già esauriti
  return {
    state: {
      ...state,
      timeoutsUsed: { ...state.timeoutsUsed, [team]: used + 1 },
      status: team === 'home' ? 'timeout_home' : 'timeout_away',
    },
    event: 'status_change',
  }
}

function applyEndTimeout(state: MatchState): ScoreResult {
  if (state.status !== 'timeout_home' && state.status !== 'timeout_away') {
    return { state, event: 'unchanged' }
  }
  return { state: { ...state, status: 'live' }, event: 'status_change' }
}

function applySetTeamInfo(
  state: MatchState,
  side: 'home' | 'away',
  name?: string,
  logo?: string,
): ScoreResult {
  const updates: Partial<MatchState> = {}
  if (side === 'home') {
    if (name !== undefined) updates.homeTeam = name
    if (logo !== undefined) updates.homeTeamLogo = logo
  } else {
    if (name !== undefined) updates.awayTeam = name
    if (logo !== undefined) updates.awayTeamLogo = logo
  }
  return { state: { ...state, ...updates }, event: 'unchanged' }
}

// ─── Utilità display ──────────────────────────────────────────────────────────

export function getMatchStatusLabel(state: MatchState): string {
  switch (state.status) {
    case 'scheduled':     return 'PROGRAMMATA'
    case 'warmup':        return 'RISCALDAMENTO'
    case 'live':          return 'IN CORSO'
    case 'pause':         return 'PAUSA'
    case 'timeout_home':  return 'TIMEOUT'
    case 'timeout_away':  return 'TIMEOUT'
    case 'finished':      return 'CONCLUSA'
    case 'postponed':     return 'RINVIATA'
  }
}

export function getSetsWonDots(setsWon: number): string {
  return Array.from({ length: SETS_TO_WIN_MATCH }, (_, i) =>
    i < setsWon ? '●' : '○'
  ).join('')
}

export function isMatchPoint(state: MatchState, team: 'home' | 'away'): boolean {
  if (state.isMatchFinished) return false
  const opTeam = team === 'home' ? 'away' : 'home'
  if (SETS_TO_WIN_MATCH - state.setsWon[team] > 1) return false
  return isSetWon(state.currentScore[team] + 1, state.currentScore[opTeam], state.currentSet)
}

export function isSetPoint(state: MatchState, team: 'home' | 'away'): boolean {
  if (state.isMatchFinished) return false
  const opTeam = team === 'home' ? 'away' : 'home'
  return isSetWon(state.currentScore[team] + 1, state.currentScore[opTeam], state.currentSet)
}
